import React from "react";
import { useEffect } from "react";
import { connect } from "react-redux";
import { getDailyAttendanceSummary } from "../../../../redux/overview/overviewState";
import AttendanceSummaryCard from "./AttendanceSummaryCard";
import AttendanceStatusCard from "./AttendanceStatusCard";
import { DATE_FORMAT } from "variables/constants";
import dayjs from "dayjs";
import {
  CircleCheck,
  CircleAlert,
  LucideCircleX,
  LucidePlaneTakeoff,
  ClipboardPen,
} from "lucide-react";

const DailyAttendances = ({ recordList, doSearch, selectedDay }) => {

  useEffect(() => {
    if (!selectedDay) return;

    doSearch({
      date: selectedDay.format(DATE_FORMAT),
    });
  }, [selectedDay, doSearch]);

  const isFutureDate = selectedDay?.isAfter(dayjs(), "day");
  
  const {
    overview = {
      present: [],
      late: [],
      absent: [],
      on_leave: [],
      filed_leave: [],
    },
    totals = {},
  } = recordList || {};

  const {
    total_present_days = 0,
    total_absent_days = 0,
    total_late_days = 0,
    total_on_leave = 0,
    total_filed_leave = 0,
  } = totals;

  const presentEmployees = (overview.present || []).map(emp => ({
    name: emp.employee_name,
    role: emp.emp_role,
    time: emp.timed_in ? dayjs(emp.timed_in).format("hh:mm A") : "-",
  }));

  const lateEmployees = (overview.late || []).map(emp => ({
    name: emp.employee_name,
    role: emp.emp_role,
    time: emp.timed_in ? dayjs(emp.timed_in).format("hh:mm A") : "-",
  }));

  const absentEmployees = (overview.absent || []).map(emp => ({
    name: emp.employee_name,
    role: emp.emp_role,
  }));

  const onLeaveEmployees = (overview.on_leave || []).map(leave => ({
    name: leave.lve_employee_name,
    role: leave.user?.userDetail?.employee?.emp_title || "",
    leave_type: leave.leave_type_name,
  }));

  const filedLeaveEmployees = (overview.filed_leave || []).map(leave => ({
    name: leave.lve_employee_name,
    role: leave.user?.userDetail?.employee?.emp_title || "",
    leave_type: leave.leave_type_name,
  }));


  const attendanceData = isFutureDate
  ? [
      {
        type: "On Leave",
        count: total_on_leave,
        employees: onLeaveEmployees,
        icon: LucidePlaneTakeoff,
        color: "blue",
      },
      {
        type: "Filed Leave",
        count: total_filed_leave,
        employees: filedLeaveEmployees,
        icon: ClipboardPen,
        color: "orange",
      },
    ]
  : [
      {
        type: "Present",
        count: total_present_days,
        employees: presentEmployees,
        icon: CircleCheck,
        color: "green",
      },
      {
        type: "Late",
        count: total_late_days,
        employees: lateEmployees,
        icon: CircleAlert,
        color: "amber",
      },
      {
        type: "Absent",
        count: total_absent_days,
        employees: absentEmployees,
        icon: LucideCircleX,
        color: "red",
      },
      {
        type: "On Leave",
        count: total_on_leave,
        employees: onLeaveEmployees,
        icon: LucidePlaneTakeoff,
        color: "blue",
      },
    ];

  return (
    <div>
      <div className="h-[85vh] pr-2 overflow-y-auto">
        <div
          className={`grid gap-4 ${
            isFutureDate
              ? "grid-cols-2"
              : "grid-cols-2 lg:grid-cols-4"
          }`}
        >
          {attendanceData.map((item) => (
            <AttendanceSummaryCard
              key={item.type}
              type={item.type}
              count={item.count}
              icon={item.icon}
            />
          ))}
        </div>

        {attendanceData.map((item) => (
          <div key={item.type} className="mt-4">
            <div className="flex items-center gap-2 mb-2">
              <item.icon
                size={20}
                className={`text-${item.color}-600`}
              />
              <span className="font-semibold text-[16px] text-gray-800 dark:text-gray-100">
                {item.type} ({item.count})
              </span>
            </div>

            <AttendanceStatusCard
              employees={item.employees}
              statusType={item.type}
            />
          </div>
        ))}
      </div>

    </div>
  );
};

const mapStateToProps = (state) => ({
  recordList: state.overview.dailyAttendanceRecord?.data,
  requesting: state.overview.dailyAttendanceRecord?.requesting,
  error: state.overview.dailyAttendanceRecord?.error,
});

const mapDispatchToProps = (dispatch) => ({
  doSearch: (filters) => dispatch(getDailyAttendanceSummary(filters)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DailyAttendances);
